// react, redux
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
// antd
import { Col, DatePicker, Form, Row, Select, Tag } from "antd"
import { CostCenter } from "@finway-group/shared/lib/models"
import moment from "moment"
import { ReportingFilterFormInterface } from "Features/pages/reporting/reportingFilter.interface"
// hooks and rules
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { generateRules } from "./rules/costCenterFilter.rules"
import { getPopupAnchor } from "Shared/utils"
import { useCompany } from "Shared/hooks/company.hooks"

const isDateOutOfRange = (current: moment.Moment) => current > moment().add(1, "M").startOf("month") || current < moment().subtract(11, "M").startOf("month")

const toggleSignal = "TOGGLE"

const ReportingFilterForm: React.FC<ReportingFilterFormInterface> = ({ formInstance, filter, onSubmit }) => {
    const { t } = useTranslation()
    const costCenters = useSubCostCenters(true, true)

    const { showCostCentersIds } = useCompany()

    const toggleMessages = {
        selectAll: t("action:filter_option.select_all"),
        deselectAll: t("action:filter_option.deselect_all"),
    }

    //Keep track of the state of the values inside formInstance
    const [reportFilter, setReportFilter] = useStateIfMounted(filter)

    const rules = useMemo(() => generateRules(formInstance), [reportFilter])

    const deselectAllCostCenterFilterOption = () => formInstance.setFieldsValue({ costCenters: [] })
    const selectAllCostCenterFilterOption = () => formInstance.setFieldsValue({ costCenters: costCenters.map((costCenter) => costCenter._id) })

    useEffect(() => {
        formInstance.validateFields()
    }, [reportFilter])

    useEffect(() => {
        formInstance.resetFields()
        setReportFilter(filter)
    }, [filter])

    const handleCostCenterFilterChange = (costCenterIds: string[]) => {
        let currentSelectedFilters = costCenterIds.filter((costCenterId: string) => costCenterId !== toggleSignal)

        if (costCenterIds.includes(toggleSignal)) {
            if (currentSelectedFilters.length > 0) {
                deselectAllCostCenterFilterOption()
                currentSelectedFilters = []
            } else {
                selectAllCostCenterFilterOption()
                currentSelectedFilters = costCenters.map((cc) => cc._id)
            }
        }

        setReportFilter({ ...reportFilter, costCenters: currentSelectedFilters })
    }

    const onSelectedCostsStyle = (props: any) => {
        const { label, closable, onClose } = props
        return (
            <Tag className="ant-tag-filter-report" closable={closable} onClose={onClose}>
                {label}
            </Tag>
        )
    }

    return (
        <div>
            <Form id="reportingFilterForm" form={formInstance} onFinish={onSubmit}>
                <div>
                    <Row gutter={[16, 16]}>
                        <Col>{t("input:reporting_filter.date_range")}</Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <Form.Item label={t("input:reporting_filter.min_date")} name="minDate" initialValue={reportFilter.minDate} rules={rules.minDate}>
                                <DatePicker
                                    name="minDate"
                                    getPopupContainer={getPopupAnchor()}
                                    className="w-full"
                                    disabledDate={isDateOutOfRange}
                                    picker="month"
                                    placeholder={t("input:reporting_filter.starting_date")}
                                    onChange={(date) => setReportFilter({ ...reportFilter, minDate: moment(date) })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label={t("input:reporting_filter.max_date")} name="maxDate" className="mb-0" initialValue={reportFilter.maxDate} rules={rules.maxDate}>
                                <DatePicker
                                    name="maxDate"
                                    getPopupContainer={getPopupAnchor()}
                                    className="w-full"
                                    disabledDate={isDateOutOfRange}
                                    picker="month"
                                    placeholder={t("input:reporting_filter.ending_date")}
                                    onChange={(date) => setReportFilter({ ...reportFilter, maxDate: moment(date) })}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col>{t("input:reporting_filter.cost_center")}</Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item name="costCenters" className="mb-0" initialValue={filter.costCenters}>
                                <Select mode="multiple" allowClear onChange={handleCostCenterFilterChange} tagRender={onSelectedCostsStyle} getPopupContainer={getPopupAnchor()}>
                                    <Select.Option value={toggleSignal}>
                                        {reportFilter.costCenters?.length > 0 ? toggleMessages.deselectAll : toggleMessages.selectAll}
                                    </Select.Option>
                                    {costCenters.map(({ _id, id, name }: CostCenter, index: number) => (
                                        <Select.Option key={index} value={_id}>
                                            {showCostCentersIds ? `ID ${id} - ${name}` : name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </Form>
        </div>
    )
}

export default ReportingFilterForm
